// src/Components/_dataset/webApiValidation.ts
import dayjs from 'dayjs';
import { 
  getColumnName, 
  isPrimitive, 
  isObject, 
  isArray, 
  getPrimitiveEntries 
} from '../DataConnection/WebApiDataSource/tableValidation';

export type ColType = 'integer' | 'decimal' | 'text' | 'boolean' | 'date' | 'timestamp';


const TIMESTAMP_FORMATS = ['YYYY-MM-DD HH:mm:ss', 'DD-MM-YYYY HH:mm:ss'];
const DATE_FORMATS = ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM/DD/YYYY', 'YYYY-M-DD'];

function matchesAnyFormat(value: string, formats: string[]) {
  return formats.some((format) => {
    const parsed = dayjs(value, format, true);
    return parsed.isValid() && parsed.format(format) === value;
  });
}

export function getType(value: any): ColType {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'string' && (value.includes('T') || matchesAnyFormat(value, TIMESTAMP_FORMATS))) return 'timestamp';
  if (typeof value === 'string' && matchesAnyFormat(value, DATE_FORMATS)) return 'date';
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'decimal';
  return 'text';
}

export function getColumns(data: any, columnName: string): { name: string; type: ColType }[] {
  if (isPrimitive(data)) return [{ name: columnName, type: getType(data) }];
  if (isObject(data)) {
    return getPrimitiveEntries(data as Record<string, unknown>).map(([key, val]) => ({
      name: key,
      type: getType(val),
    }));
  }
  if (isArray(data)) {
    const arr = data as unknown[];
    if (arr.every(isPrimitive)) return [{ name: 'Value', type: getType(arr[0]) }];
    if (arr.every(isObject)) {
      const allKeys = [...new Set((arr as any[]).flatMap((item) => Object.keys(item)))];
      return allKeys.map((key) => {
        const firstVal = (arr as any[]).map((item) => item[key]).find((v) => v !== undefined && v !== null);
        return { name: key, type: getType(firstVal) };
      });
    }
  }
  return [];
}

// validator
export const validateWebApiTable = (table: any, responseData: any): boolean => {
  if (!responseData || !table.customQuery || !table.isWebApi) return true;

  const queryObj = (() => {
    try {
      return JSON.parse(table.customQuery);
    } catch {
      return null;
    }
  })();

  if (!queryObj || typeof queryObj !== 'object') return true;

  const jsonPath: (string | number)[] = queryObj.jsonPath || [];
  const actualPath = jsonPath[0] === 'response' ? jsonPath.slice(1) : jsonPath;

  let liveValue = responseData;
  for (const key of actualPath) {
    const cleanKey = typeof key === 'string' && key.startsWith('#')
      ? Number.parseInt(key.substring(1), 10)
      : key;
    liveValue = liveValue?.[cleanKey];
  }

  if (liveValue === undefined || liveValue === null) return false;

  const liveColName = getColumnName(actualPath);
  const liveColumns = getColumns(liveValue, liveColName);
  const savedSchema = Array.isArray(queryObj.columns) ? queryObj.columns : [];

  
  if (savedSchema.length === 0) return true;
  if (liveColumns.length === 0) return false;

  const areTypesCompatible = (savedType: ColType, liveType: ColType) => {
    if (savedType === liveType) return true;
    if (savedType === 'integer' && liveType === 'decimal') return true;
    if (savedType === 'decimal' && liveType === 'integer') return true;
    if (savedType === 'text') return true;
    return false;
  };

  return savedSchema.every((savedCol: any) => {
    const matchingLive = liveColumns.find((lc: any) => lc.name === savedCol.fieldName);
    return !!matchingLive && areTypesCompatible(savedCol.dataType, matchingLive.type);
  });
};